import { saveAs } from 'file-saver';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, createRef, Fragment } from 'react';
import DropzoneComponent from 'react-dropzone-component';
import { FormattedMessage, injectIntl } from 'react-intl';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { handle403 } from '~/api/errorHandlers';
import {
  BULK_COPY_LIMIT,
  BULK_DELETE_LIMIT,
  BULK_EDIT_LIMIT,
  BULK_MOVE_LIMIT,
} from '~/common/constants/max_lengths';
import { PAGE_SIZE, PAGE_START } from '~/common/constants/page';
import AddToGroup from '~/components/DocumentsPage/AddToGroup/AddToGroup';
import styles from '~/components/DocumentsPage/DocumentsPage.styles';
import DocumentsPageHeader from '~/components/DocumentsPage/DocumentsPageHeader/DocumentsPageHeader';
import DocumentsTable from '~/components/DocumentsPage/DocumentsTable/DocumentsTable';
import { DocumentsTableV2 } from '~/components/DocumentsPage/DocumentsTable/DocumentsTableV2';
import {
  bulkCopy,
  bulkDelete,
  bulkMove,
  bulkUpdate,
  createFolder,
  editDocumentsViewsetFields,
  getDocumentOCR,
  getFolderData,
  getGroupsForDocuments,
  getMultiEditData,
  getUserCanModifyFolder,
  renameDocument,
  renameFolder,
  updateFolderAccessSettings,
} from '~/components/DocumentsViewPage/Document.data';
import AccessSettingsModal from '~/components/Modals/AccessSettingsModal/AccessSettingsModal';
import DeleteDocumentWithReasonModal from '~/components/Modals/DeleteDocumentWithReasonModal/DeleteDocumentWithReasonModal';
import { trackSegment } from '~/components/SegmentAnalytics';
import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import BaseLoadView from '~/components/Shared/BaseLoadView/BaseLoadView';
import BaseSaveView from '~/components/Shared/BaseSaveView/BaseSaveView';
import EcModal from '~/components/Shared/EcModal/EcModal';
import EcPaginate from '~/components/Shared/EcPaginate/EcPaginate';
import EcSelect from '~/components/Shared/EcSelect/EcSelect';
import { showToast } from '~/components/Shared/EcToast/showToast';
import CheckmarkIcon from '~/components/Shared/Icons/CheckmarkIcon';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import PlusIcon from '~/components/Shared/Icons/PlusIcon';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { QueryParamType } from '~/enums/QueryParamType';
import { TableContextType } from '~/enums/TableContextType';
import {
  getDefaultChatContext,
  getFolderQuery,
} from '~/features/ask-anything/ask-anything-button/utils';
import { DOCUMENT_NAME_MAX_LENGTH } from '~/features/document-handler/constants';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withPermissions } from '~/hocs/withPermissions';
import { api } from '~/redux/api/createApi';
import { getDocumentOriginal } from '~/redux/api/methods/document';
import { getDocumentsCount } from '~/redux/api/methods/documents';
import {
  getFolderHandlersByPosition,
  getFolderPositionByHandler,
} from '~/redux/api/methods/search';
import { TagType } from '~/redux/api/TagType';
import chatbot from '~/redux/slices/chatbot';
import { initContext } from '~/redux/slices/documentsNavigation';
import { uploadDocumentsThunk } from '~/redux/thunks/fileUpload';
import { withRouting } from '~/routing/withRouting';
import {
  MODAL_ACCESS_SETTINGS,
  MODAL_DELETE,
  MODAL_DEPARTMENT_TREE,
  MODAL_DOCUMENT_GROUP,
  MODAL_DOCUMENTS_COLUMN_FIELDS,
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
  MODAL_FOLDER_TREE,
  MODAL_MULTI_EDIT,
  MODAL_NAME,
  MODAL_UPLOAD,
} from '~/types/modal.types';
import { ERROR, SUCCESS, WARNING } from '~/types/toast.types';
import { getStorageItem } from '~/utils/browser';
import { filterValidFiles } from '~/utils/helper.utils';
import { parseNavigationResponse as parseResponse } from '~/utils/navigation';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';
import { getStorageKey } from '~/utils/table';
import {
  featureFlagIncluded,
  getClientInfo,
  getUserClientInfo,
  testIsAdmin,
} from '~/utils/user';

const folderAccessPermission = {
  resourceId: 'folder_access',
  resourceType: 'edit',
};

const permissionsToCheck = [folderAccessPermission];

// TODO: break down this complex component and manage state logic in Redux
class Page extends Component {
  constructor(props) {
    super(props);
    this.tableV2Ref = createRef();
    this.onFolderCreate = this.onFolderCreate.bind(this);
    this.onUpdateFolderAccessSettings =
      this.onUpdateFolderAccessSettings.bind(this);
    this.onFileFolderRename = this.onFileFolderRename.bind(this);
    this.onBulkDelete = this.onBulkDelete.bind(this);
    this.onBulkMoveCopy = this.onBulkMoveCopy.bind(this);
    this.handleUploadLocationChange =
      this.handleUploadLocationChange.bind(this);
    this.handleAddUploadFiles = this.handleAddUploadFiles.bind(this);
    this.handleRemoveUploadFile = this.handleRemoveUploadFile.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleShowSecondaryModal = this.handleShowSecondaryModal.bind(this);
    this.handleHideSecondaryModal = this.handleHideSecondaryModal.bind(this);
    this.handlePageClick = this.handlePageClick.bind(this);
    this.handleDepartmentChange = this.handleDepartmentChange.bind(this);
    this.updateDepartmentsArray = this.updateDepartmentsArray.bind(this);
    this.handleSaveEditColumns = this.handleSaveEditColumns.bind(this);
    this.handleRowSelect = this.handleRowSelect.bind(this);
    this.handleSortClick = this.handleSortClick.bind(this);
    this.handleErrorLoadFolderData = this.handleErrorLoadFolderData.bind(this);
    this._updateDocuments = this._updateDocuments.bind(this);
    this.handleOnSelectPageClick = this.handleOnSelectPageClick.bind(this);
    this.handleOnSelectAllClick = this.handleOnSelectAllClick.bind(this);
    this.handleOnConfirmDelete = this.handleOnConfirmDelete.bind(this);

    this.accessSettingsModalRef = null;

    this.state = {
      isLoading: true,
      isFetching: true,
      folderData: {},
      configuredColumns: [],
      isAllSelected: false,
      isPageSelected: false,
      uploadLocation: null,
      uploadFiles: [],
      primaryModal: null,
      secondaryModal: null,
      sortedColumn: getPageSearchQueryByKey(
        QueryParamType.SortedColumn,
        'name',
      ),
      sortedOrder: getPageSearchQueryByKey(QueryParamType.SortedOrder, 'asc'),
      page: getPageSearchQueryByKey(QueryParamType.Page, PAGE_START),
      pageCount: 1,
      pageSize: getPageSearchQueryByKey(QueryParamType.PageSize, PAGE_SIZE),
      selectedItems: getPageSearchQueryByKey(QueryParamType.SelectedItems, []),
      canModifyFolder: false,
      departmentsSelected: [],
      hasUploadPermission: null,
    };
  }

  componentDidMount() {
    const {
      params: { folderId },
      currentUser,
      flags,
    } = this.props;
    const { page, pageSize, sortedColumn, sortedOrder, selectedItems } =
      this.state;
    let localStorageTableState;
    if (flags[FlagType.DocumentsTableRevamp]) {
      localStorageTableState = getStorageItem(
        getStorageKey(currentUser, TableContextType.Documents),
      );
    }
    const consolidatedSortedOrder = localStorageTableState?.sortBy
      ? localStorageTableState.sortBy.desc
        ? 'desc'
        : 'asc'
      : sortedOrder;
    const urlParams = {
      page: page,
      pageSize: localStorageTableState?.pageSize ?? pageSize,
      sortedOrder: consolidatedSortedOrder,
      sortedColumn: localStorageTableState?.sortBy.id ?? sortedColumn,
      selectedItems: selectedItems,
    };

    this.setState(
      {
        ...urlParams,
      },
      () => {
        this.loadFolderData({ isFirstLoad: true });
        updatePageSearchQuery(urlParams);
      },
    );

    this.initializeDropzone();

    getUserCanModifyFolder(folderId)
      .then((data) => {
        const canModifyFolder = data.can_modify_folder;
        this.setState({
          canModifyFolder,
          hasUploadPermission: canModifyFolder,
        });
      })
      .catch(handle403);
  }

  componentDidUpdate(prevProps, prevState) {
    const { flags } = this.props;
    const { folderId } = this.props.params;
    const { sortedColumn, sortedOrder, pageSize, page } = this.state;

    if (prevState.uploadLocation?.id !== this.state.uploadLocation?.id) {
      getUserCanModifyFolder(this.state.uploadLocation.id)
        .then((res) => {
          this.setState({ hasUploadPermission: res['can_modify_folder'] });
        })
        .catch(handle403);
    }

    if (folderId !== prevProps.params.folderId) {
      const firstPage = 1;
      this.setState({ isLoading: true, isFetching: true, page: firstPage });
      getUserCanModifyFolder(folderId)
        .then((data) => {
          this.setState({ canModifyFolder: data.can_modify_folder });
        })
        .catch(handle403);

      this.loadFolderData();
    }
    if (prevState.primaryModal === MODAL_UPLOAD && !this.state.primaryModal) {
      this.setState(
        { folderData: prevState.folderData, uploadFiles: [] },
        this.addGroupInfo,
      );
    }
    if (prevState.folderData !== this.state.folderData) {
      this.setState({
        secondaryModal: null,
        folderData: this.state.folderData,
      });
    }
    if (
      prevState.selectedItem &&
      this.state.selectedItem &&
      prevState.selectedItem !== this.state.selectedItem &&
      this.state.selectedItem &&
      this.state.selectedItem.folder_access_list
    ) {
      this.setState({
        departmentsSelected:
          this.state.selectedItem.folder_access_list.departments,
      });
    }
    const currentSearchParams = { page, pageSize, sortedColumn, sortedOrder };
    const prevStateSearchParams = {
      page: prevState.page,
      pageSize: prevState.pageSize,
      sortedColumn: prevState.sortedColumn,
      sortedOrder: prevState.sortedOrder,
    };
    if (
      flags[FlagType.DocumentsTableRevamp] &&
      !isEqual(currentSearchParams, prevStateSearchParams)
    ) {
      /**
       * When DocumentsTableRevamp flag is OFF, the function to load folder data is called
       * in each onChange function of the table. While with the new table, only the state is updated and
       * the call to load data is consolidated here in a single callsite when the params change.
       */
      this.loadFolderData();
    }
  }

  initializeNavigationContext = ({ type }) => {
    if (type === 'folder') return;

    const { folderData, sortedColumn, sortedOrder } = this.state;
    const { location } = this.props;
    const fromLocation = { ...location, search: window.location.search };

    const { id: folderId, name } = folderData;

    const api = (position, folder) =>
      getFolderHandlersByPosition({
        position,
        folderId: folder,
        orderByField: sortedColumn,
        order: sortedOrder,
      });
    const getPosition = (handler, folder) =>
      getFolderPositionByHandler({
        handlerId: handler,
        folderId: folder,
        orderByField: sortedColumn,
        order: sortedOrder,
      });

    const initOptions = {
      api,
      name,
      parseResponse,
      getPosition,
      fromLocation,
      folderId,
    };
    this.props.initContext(initOptions);
  };

  initializeDropzone() {
    const dzComponentConfig = { postUrl: 'no-url' };
    const dzConfig = {
      autoQueue: false,
      autoProcessQueue: false,
      dictDefaultMessage: '',
      previewTemplate: '<div class="ignore"></div>',
    };
    const dzEventHandlers = {
      init: (dz) => (this.dropzone = dz),
      addedfiles: async (files) => {
        const validFiles = await filterValidFiles(files);
        if (validFiles.length > 0) {
          setTimeout(() => {
            this.handleAddUploadFiles(validFiles);
            this.handleShowModal(MODAL_UPLOAD);
          }, 0);
        }
      },
    };

    this.setState({ dzComponentConfig, dzConfig, dzEventHandlers });
  }

  _updateDocuments(documentDataArray) {
    const { folderData } = this.state;

    for (let newData of documentDataArray) {
      const documentIndex = folderData.results
        ? folderData.results.findIndex(
            ({ id, type }) => id === newData.documentId && type === 'document',
          )
        : -1;
      if (documentIndex !== -1) {
        folderData.results[documentIndex] = {
          ...folderData.results[documentIndex],
          groups: newData.results,
        };
      }
    }

    this.setState({ folderData });
  }

  addGroupInfo() {
    const { folderData } = this.state;
    const { intl } = this.props;
    const docIds = [];
    for (let folderOrDoc of folderData.results) {
      folderOrDoc.groups = [];
      if (folderOrDoc.type === 'document') {
        docIds.push(folderOrDoc.id);
      }
    }

    const handleErrorLoadGroupData = () =>
      showToast(
        WARNING,
        intl.formatMessage({
          id: 'DocumentsFolderId.errorLoadingGroupInfo',
          defaultMessage: 'There was an error loading group information',
          description: 'Error message when group information fails to load',
        }),
      );
    return getGroupsForDocuments(
      docIds,
      this._updateDocuments,
      handleErrorLoadGroupData,
    );
  }

  _loadFolderData(
    folderId,
    sortedColumn,
    sortedOrder,
    page,
    pageSize,
    isFirstLoad = false,
  ) {
    const items = getPageSearchQueryByKey(QueryParamType.SelectedItems, []);
    const { intl, setAskAIContext } = this.props;

    this.setState({
      isLoading: isFirstLoad,
      isFetching: true,
    });
    getFolderData(folderId, sortedColumn, sortedOrder, page, pageSize)
      .then((data) => {
        const isRoot = data?.path?.length === 0;
        const defaultContext = getDefaultChatContext(intl);
        const folderContext = {
          label:
            intl?.formatMessage({
              id: 'Utils.thisFolderLabel',
              defaultMessage: 'This Folder',
              description: 'Label for current folder context',
            }) ?? '',
          query: getFolderQuery(folderId),
          title: data.name,
          icon: 'field-folder',
          primary: true,
          searchEntity: 'document',
          selectedIds: [],
          id: data.id.toString(),
          type: 'folder',
        };
        if (isRoot) {
          setAskAIContext([{ ...defaultContext, primary: true }]);
        } else {
          const context = [defaultContext, folderContext];
          setAskAIContext(context);
        }

        this.setState(
          {
            folderData: data,
            configuredColumns: data.results.length
              ? data.results[0].document_fields
              : [],
            uploadLocation: data,
            pageCount: data.count,

            selectedItems: items,
          },
          this.addGroupInfo,
        );
      })
      .catch(() => this.handleErrorLoadFolderData())
      .finally(() => {
        this.setState({
          isLoading: false,
          isFetching: false,
        });
      });
  }

  loadFolderData = ({ isFirstLoad = false } = {}) => {
    const { folderId } = this.props.params;
    const { sortedColumn, sortedOrder, page, pageSize } = this.state;
    this._loadFolderData(
      folderId,
      sortedColumn,
      sortedOrder,
      page,
      pageSize,
      isFirstLoad,
    );
  };

  reloadPage() {
    window.location.reload();
  }

  handleErrorLoadFolderData() {
    this.setState({
      errorLoadingMessage: true,
    });
  }

  handleSortClick(columnName) {
    const { flags } = this.props;
    const disableSortByField =
      flags[FlagType.FieldValueMigrationDeferredFeaturesBypass] ||
      flags[FlagType.DisableDocumentTableSorting] ||
      flags[FlagType.FolderContentPrefetch];
    if (columnName === 'cog' || columnName === 'selectAll') return;
    else if (columnName === 'name') {
      this.setState(
        {
          page: PAGE_START,
          sortedOrder: this.state.sortedOrder === 'asc' ? 'desc' : 'asc',
          sortedColumn: 'name',
        },
        () => {
          this.loadFolderData();
          updatePageSearchQuery({
            [QueryParamType.SortedColumn]: 'name',
            [QueryParamType.SortedOrder]: this.state.sortedOrder,
          });
        },
      );
    } else if (disableSortByField) {
      // We don't want to sort by field if disableSortByField is true
      return;
    } else {
      const { configuredColumns } = this.state;

      this.setState(
        {
          page: PAGE_START,
          sortedOrder:
            configuredColumns.filter(
              (column) => column.field_name === columnName,
            ).length &&
            configuredColumns.filter(
              (column) => column.field_name === columnName,
            )[0].field_name === columnName &&
            this.state.sortedOrder === 'asc'
              ? 'desc'
              : 'asc',
          sortedColumn:
            configuredColumns.filter(
              (column) => column.field_name === columnName,
            ).length &&
            configuredColumns.filter(
              (column) => column.field_name === columnName,
            )[0].field_id,
        },
        () => {
          this.loadFolderData();
          updatePageSearchQuery({
            [QueryParamType.SortedColumn]: this.state.sortedColumn,
            [QueryParamType.SortedOrder]: this.state.sortedOrder,
          });
        },
      );
    }
  }

  handlePageSizeOnChange = (value) => {
    this.setState({ pageSize: value, page: 1 }, () => {
      this.loadFolderData();
      updatePageSearchQuery({
        [QueryParamType.Page]: 1,
        [QueryParamType.PageSize]: value,
      });
    });
  };

  updateTableState = ({ page, pageSize, sortBy }) => {
    const sortedColumn = sortBy.id;
    const sortedOrder = sortBy.desc ? 'desc' : 'asc';
    this.setState({ page, pageSize, sortedColumn, sortedOrder }, () => {
      updatePageSearchQuery(
        {
          [QueryParamType.Page]: page,
          [QueryParamType.PageSize]: pageSize,
          [QueryParamType.SortedColumn]: sortedColumn,
          [QueryParamType.SortedOrder]: sortedOrder,
        },
        true,
      );
    });
  };

  transformSelectedItemsForPages(selectedItemsOnCurrentPage) {
    const {
      folderData: { results },
      selectedItems,
    } = this.state;

    const currentPageItemIds = results.map((item) => item.id);
    const selectedItemsOnOtherPages = selectedItems.filter(
      (item) => !currentPageItemIds.includes(item.id),
    );
    return {
      selectedItemsOnOtherPages,
      combinedData: [
        ...selectedItemsOnOtherPages,
        ...selectedItemsOnCurrentPage,
      ],
    };
  }

  handleRowSelect(rowsSelected) {
    const selectedItemsOnCurrentPage = rowsSelected.map((item) => {
      return {
        document_id: item.original.document_id, // add to group
        id: item.original.id, // edit copy move
        name: item.original.name, // delete
        type: item.original.type, // copy move
        contains_sync_pair: item.original.contains_sync_pair, // move
      };
    });
    const { combinedData } = this.transformSelectedItemsForPages(
      selectedItemsOnCurrentPage,
    );

    this.setState({ selectedItems: combinedData, isAllSelected: false }, () => {
      updatePageSearchQuery({ [QueryParamType.SelectedItems]: combinedData });
    });
  }

  handlePageClick(clickedPage) {
    const selectedPage = clickedPage.selected + 1;
    this.setState({ page: selectedPage }, () => {
      updatePageSearchQuery({ [QueryParamType.Page]: selectedPage }, true);
      this.loadFolderData();
    });
  }

  handleOnSelectPageClick(isPageSelected) {
    const {
      folderData: { results },
    } = this.state;

    const selectedItemsOnCurrentPage = results.map((item) => {
      return {
        document_id: item.document_id, // add to group
        id: item.id, // edit copy move
        name: item.name, // delete
        type: item.type, // copy move
      };
    });
    const { combinedData, selectedItemsOnOtherPages } =
      this.transformSelectedItemsForPages(selectedItemsOnCurrentPage);
    const newSelectedItems = isPageSelected
      ? combinedData
      : selectedItemsOnOtherPages;

    this.setState({ isPageSelected, selectedItems: newSelectedItems }, () => {
      updatePageSearchQuery({
        [QueryParamType.SelectedItems]: newSelectedItems,
      });
    });
  }

  handleOnSelectAllClick(isAllSelected) {
    this.setState({ isAllSelected });
  }

  handleUploadLocationChange(uploadLocation) {
    this.setState({ uploadLocation });
  }

  handleAddUploadFiles(files) {
    const newFiles = Object.keys(files).map((key) => files[key]);
    this.setState({ uploadFiles: [...this.state.uploadFiles, ...newFiles] });
  }

  handleRemoveUploadFile(uploadId) {
    const newStateFiles = this.state.uploadFiles.filter(
      (file) => file.upload.uuid !== uploadId,
    );
    this.setState({ uploadFiles: newStateFiles });
  }

  handleShowModal = (modal, selectedItem, modalBulkAction = null) => {
    const { flags } = this.props;
    this.setState({
      primaryModal: modal,
      modalBulkAction,
      selectedItems:
        Array.isArray(selectedItem) && flags[FlagType.DocumentsTableRevamp]
          ? selectedItem
          : this.state.selectedItems,
      selectedItem,
      departmentsSelected:
        selectedItem && selectedItem.folder_access_list
          ? selectedItem.folder_access_list.departments
          : [],
    });
  };

  handleHideModal() {
    this.setState({
      primaryModal: null,
      modalBulkAction: null,
      selectedItem: null,
    });
  }

  handleShowSecondaryModal(modal) {
    this.setState({ secondaryModal: modal });
  }

  handleHideSecondaryModal() {
    this.setState({ secondaryModal: null });
  }

  handleOnConfirmDelete(deleteReason, deleteComment) {
    const { selectedItem, selectedItems, folderData } = this.state;
    const { navigate, intl } = this.props;
    const [documents, folders, select_all] = this.setBulkParams();
    bulkDelete(
      selectedItem?.selfUpdate ? selectedItem.parentId : folderData.id,
      documents,
      folders,
      select_all,
      deleteReason,
      deleteComment,
      true,
    )
      .then((res) => {
        if (res.skipped_folder_names) {
          const skipped = res.skipped_folder_names.join(', ');
          const warningMessage = intl.formatMessage(
            {
              id: 'DocumentsFolderId.foldersSkippedSyncWarning',
              defaultMessage:
                'The following folder(s) were skipped because they contain synchronization(s) with external storage provider(s): {skipped}',
              description:
                'Warning message when folders with sync are skipped. Example: comma-separated list of folder names.',
            },
            { skipped },
          );
          showToast(WARNING, warningMessage);
        } else {
          const successMessage =
            !selectedItems || selectedItems.length
              ? intl.formatMessage({
                  id: 'DocumentsFolderId.bulkDeleteSuccess',
                  defaultMessage: 'Selected items have been deleted.',
                  description: 'Success message for bulk delete',
                })
              : intl.formatMessage(
                  {
                    id: 'DocumentsFolderId.singleDeleteSuccess',
                    defaultMessage: '"{itemName}" has been deleted.',
                    description:
                      'Success message for single item delete. itemName: name of deleted item. Example: itemName = "contract.pdf"',
                  },
                  { itemName: selectedItem.name },
                );
          showToast(SUCCESS, successMessage);
        }

        if (selectedItem?.selfUpdate) {
          navigate(-1);
        }

        this.updatePageAfterBulkAction(documents);
        if (folders.length > 0 || select_all) {
          this.props.dispatch(
            api.util.invalidateTags([{ type: TagType.Folder, id: 'TREE' }]),
          );
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 409) {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'DocumentsFolderId.folderDeleteSyncError',
              defaultMessage:
                'Could not delete this folder, it contains a synchronization with an external storage provider.',
              description:
                'Error message when trying to delete folder with sync',
            }),
          );
        } else {
          const errorMessage =
            !selectedItems || selectedItems.length
              ? intl.formatMessage({
                  id: 'DocumentsFolderId.bulkDeleteError',
                  defaultMessage:
                    'An error occurred while deleting these items.',
                  description: 'Generic error message for bulk delete failure',
                })
              : intl.formatMessage(
                  {
                    id: 'DocumentsFolderId.singleDeleteError',
                    defaultMessage:
                      'An error occurred while deleting this {itemType}',
                    description:
                      'Generic error message for single item delete failure. Example: itemType: "document" or "folder"',
                  },
                  { itemType: selectedItem.type },
                );

          showToast(WARNING, errorMessage);
        }

        this.setState({ isAllSelected: false, isPageSelected: false });
      });
  }

  showNameCollisionErrorToast = () => {
    const { intl } = this.props;
    showToast(
      ERROR,
      intl.formatMessage({
        id: 'DocumentsFolderId.nameCollisionError',
        defaultMessage:
          'There is a folder somewhere in your organization with the same name. Please choose a different name.',
        description: 'Error message when folder name already exists',
      }),
    );
  };

  onFolderCreate(folderName, parentId) {
    const { intl } = this.props;
    createFolder(folderName, parentId)
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'DocumentsFolderId.folderCreatedSuccess',
            defaultMessage: 'Folder has been created.',
            description: 'Success message when folder is created',
          }),
        );
        this.loadFolderData();
        this.handleHideModal();
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          this.showNameCollisionErrorToast();
        } else if (err.response && err.response.status === 422) {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentsFolderId.folderNameSlashError',
              defaultMessage:
                "A forward slash '/' is not allowed in a folder name. Please choose a different name.",
              description:
                'Error message when folder name contains forward slash',
            }),
          );
        } else {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'DocumentsFolderId.folderCreationError',
              defaultMessage: 'An error occurred while creating a new folder.',
              description: 'Generic error message for folder creation failure',
            }),
          );
        }
      });
  }

  onUpdateFolderAccessSettings(
    folderId,
    folderName,
    parentId,
    visibilityLevel,
    folderAccessList,
    departmentAccessList,
  ) {
    const { selectedItem } = this.state;
    const { intl } = this.props;
    updateFolderAccessSettings(
      folderId,
      folderName,
      selectedItem?.selfUpdate ? selectedItem.parentId : parentId,
      visibilityLevel,
      folderAccessList,
      departmentAccessList,
    )
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'DocumentsFolderId.folderUpdatedSuccess',
            defaultMessage: 'Folder has been updated.',
            description: 'Success message when folder is updated',
          }),
        );
        this.loadFolderData();
        this.handleHideModal();
      })
      .catch(() => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'DocumentsFolderId.folderAccessUpdateError',
            defaultMessage:
              'An error occurred while updating the folder access settings.',
            description:
              'Error message when folder access settings update fails',
          }),
        );
      });
  }

  onFileFolderRename(itemName, parentId) {
    const { selectedItem } = this.state;
    const { intl } = this.props;

    const isDocument = selectedItem.type === 'document';
    const selectedItemsUrl = isDocument
      ? `/document/${selectedItem.id}`
      : `/documents/${selectedItem.id}`;
    const renameItem = isDocument ? renameDocument : renameFolder;

    this.setState({ isLoading: true });

    renameItem(selectedItem.id, itemName, parentId)
      .then(() => {
        const newFolderData = { ...this.state.folderData };
        newFolderData.results = newFolderData.results.map((item) => {
          if (item.id === selectedItem.id && item.type === selectedItem.type) {
            item.name = itemName;
          }
          return item;
        });

        this.setState(
          {
            folderData: selectedItem?.selfUpdate
              ? { ...newFolderData, name: itemName }
              : newFolderData,
          },
          showToast(
            SUCCESS,
            intl.formatMessage(
              {
                id: 'DocumentsFolderId.itemRenamedSuccess',
                defaultMessage: '"{itemName}" has been renamed.',
                description:
                  'Success message when item is renamed. itemName: name of the renamed item. Example: itemName = "contract.pdf"',
              },
              { itemName: selectedItem.name },
            ),
            selectedItemsUrl,
          ),
        );
      })
      .catch((err) => {
        if (err?.response?.data?.non_field_errors?.[0]) {
          showToast(ERROR, err.response.data.non_field_errors[0]);
        } else if (err.response && err.response.status === 400) {
          this.showNameCollisionErrorToast();
        } else if (err.response && err.response.status === 422) {
          showToast(
            ERROR,
            intl.formatMessage(
              {
                id: 'DocumentsFolderId.itemNameSlashError',
                defaultMessage:
                  "A forward slash '/' is not allowed in a {itemType} name. Please choose a different name.",
                description:
                  'Error message when item name contains forward slash. itemType: "document" or "folder". Example: itemType = "document"',
              },
              { itemType: selectedItem.type },
            ),
          );
        } else {
          showToast(
            ERROR,
            intl.formatMessage(
              {
                id: 'DocumentsFolderId.itemRenameError',
                defaultMessage:
                  'An error occurred while renaming this {itemType}',
                description:
                  'Generic error message for item rename failure. itemType: "document" or "folder". Example: itemType = "folder"',
              },
              { itemType: selectedItem.type },
            ),
          );
        }
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  updatePageAfterBulkAction = (documents) => {
    // after a bulk action involving all selected documents, redirect to the relevant computed next page
    const { folderData, pageCount, page: currentPage, pageSize } = this.state;

    const maxPage = Math.ceil(pageCount / pageSize);
    let nextPage = currentPage;
    if (documents.length === folderData.results.length && currentPage > 1) {
      nextPage = currentPage >= maxPage ? 1 : currentPage + 1;
    }

    this.setState(
      {
        selectedItems: [],
        isAllSelected: false,
        isPageSelected: false,
        page: nextPage,
      },
      () => {
        updatePageSearchQuery({ [QueryParamType.SelectedItems]: [] });
        if (this.tableV2Ref.current) {
          this.tableV2Ref.current.clearSelectedRows?.();
        }
        this.loadFolderData();
      },
    );
  };

  onBulkMoveCopy(location) {
    const { selectedItem, selectedItems, folderData, modalBulkAction } =
      this.state;
    const { intl } = this.props;
    const bulkAction =
      (modalBulkAction ?? selectedItem.action) === 'Move' ? bulkMove : bulkCopy;
    const [documents, folders, select_all] = this.setBulkParams();

    bulkAction(
      selectedItem?.selfUpdate ? selectedItem.parentId : folderData.id,
      documents,
      folders,
      location.id,
      select_all,
    )
      .then((res) => {
        if (res.skipped_folder_names) {
          const skipped = res.skipped_folder_names.join(', ');
          const warningMessage = intl.formatMessage(
            {
              id: 'DocumentsFolderId.foldersSkippedWarning',
              defaultMessage:
                'The following folders were skipped because moving them would create overlapping external synchronizations: {skipped}',
              description:
                'Warning message when folders are skipped during move. skipped: comma-separated list of folder names. Example: skipped = "Contracts, Legal Documents"',
            },
            { skipped },
          );
          showToast(WARNING, warningMessage);
        } else {
          const successVerb =
            modalBulkAction === 'Move'
              ? intl.formatMessage({
                  id: 'DocumentsFolderId.moved',
                  defaultMessage: 'moved',
                  description: 'Past tense verb for move action',
                })
              : intl.formatMessage({
                  id: 'DocumentsFolderId.copied',
                  defaultMessage: 'copied',
                  description: 'Past tense verb for copy action',
                });
          const successMessage =
            !selectedItems || selectedItems.length
              ? intl.formatMessage(
                  {
                    id: 'DocumentsFolderId.bulkActionSuccess',
                    defaultMessage:
                      'Selected items have been {action} to "{locationName}".',
                    description:
                      'Success message for bulk move/copy. action: "moved" or "copied", locationName: destination folder name. Example: action = "moved", locationName = "Archive"',
                  },
                  { action: successVerb, locationName: location.name },
                )
              : intl.formatMessage(
                  {
                    id: 'DocumentsFolderId.singleActionSuccess',
                    defaultMessage:
                      '"{itemName}" has been {action} to "{locationName}".',
                    description:
                      'Success message for single item move/copy. itemName: name of item, action: "moved" or "copied", locationName: destination folder name. Example: itemName = "contract.pdf", action = "moved", locationName = "Archive"',
                  },
                  {
                    itemName: selectedItem.name,
                    action: successVerb,
                    locationName: location.name,
                  },
                );
          showToast(SUCCESS, successMessage);
        }
        if (selectedItem?.selfUpdate) {
          this.loadFolderData();
        } else {
          this.updatePageAfterBulkAction(documents);
        }
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.error ||
          intl.formatMessage({
            id: 'DocumentsFolderId.actionFailedError',
            defaultMessage: 'Action has failed to execute.',
            description: 'Generic error message for failed action',
          });
        showToast(ERROR, errorMessage);
        this.setState({ isAllSelected: false, isPageSelected: false });
      });
  }

  onBulkDelete() {
    const { selectedItem, selectedItems, folderData } = this.state;
    const { navigate, intl } = this.props;
    const [documents, folders, select_all] = this.setBulkParams();

    bulkDelete(
      selectedItem?.selfUpdate ? selectedItem.parentId : folderData.id,
      documents,
      folders,
      select_all,
      null,
      null,
      true,
    )
      .then((res) => {
        if (res.skipped_folder_names) {
          const skipped = res.skipped_folder_names.join(', ');
          const warningMessage = intl.formatMessage(
            {
              id: 'DocumentsFolderId.foldersSkippedSyncWarning2',
              defaultMessage:
                'The following folder(s) were skipped because they contain synchronization(s) with external storage provider(s): {skipped}',
              description:
                'Warning message when folders with sync are skipped. Example: skipped = "Legal Documents, Contracts"',
            },
            { skipped },
          );
          showToast(WARNING, warningMessage);
        } else {
          const successMessage =
            !selectedItems || selectedItems.length
              ? intl.formatMessage({
                  id: 'DocumentsFolderId.bulkDeleteSuccess',
                  defaultMessage: 'Selected items have been deleted.',
                  description: 'Success message for bulk delete',
                })
              : intl.formatMessage(
                  {
                    id: 'DocumentsFolderId.singleDeleteSuccess',
                    defaultMessage: '"{itemName}" has been deleted.',
                    description:
                      'Success message for single item delete. itemName: name of deleted item. Example: itemName = "contract.pdf"',
                  },
                  { itemName: selectedItem.name },
                );
          showToast(SUCCESS, successMessage);
        }
        if (selectedItem?.selfUpdate) {
          navigate(-1);
        } else {
          this.updatePageAfterBulkAction(documents);
        }
        if (folders.length > 0 || select_all) {
          this.props.dispatch(
            api.util.invalidateTags([{ type: TagType.Folder, id: 'TREE' }]),
          );
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 409) {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'DocumentsFolderId.folderDeleteSyncError',
              defaultMessage:
                'Could not delete this folder, it contains a synchronization with an external storage provider.',
              description:
                'Error message when trying to delete folder with sync',
            }),
          );
        } else {
          const genericMessage =
            !selectedItems || selectedItems.length
              ? intl.formatMessage({
                  id: 'DocumentsFolderId.bulkDeleteError2',
                  defaultMessage:
                    'An error occurred while deleting these items.',
                  description: 'Generic error message for bulk delete failure',
                })
              : intl.formatMessage(
                  {
                    id: 'DocumentsFolderId.singleDeleteError',
                    defaultMessage:
                      'An error occurred while deleting this {itemType}',
                    description:
                      'Generic error message for single item delete failure. Example: itemType: "document" or "folder"',
                  },
                  { itemType: selectedItem.type },
                );

          const errorMessage = err.response?.data?.error || genericMessage;
          showToast(WARNING, errorMessage);
        }

        this.setState({ isAllSelected: false, isPageSelected: false });
      });
  }

  setBulkParams() {
    const { selectedItem, selectedItems, isAllSelected } = this.state;

    let documents = [];
    let folders = [];
    let select_all = false;

    if (isAllSelected) {
      select_all = true;
    } else {
      if (selectedItems) {
        if (selectedItems.length) {
          selectedItems.forEach((item) =>
            item.type === 'folder'
              ? folders.push(item.id)
              : documents.push(item.id),
          );
        } else {
          selectedItem.type === 'folder'
            ? folders.push(selectedItem.id)
            : documents.push(selectedItem.id);
        }
      }
    }

    return [documents, folders, select_all];
  }

  handleOnDownloadOriginalClick(item) {
    const { id, name, file_type } = item;
    const { intl } = this.props;

    getDocumentOriginal(id)
      .then((res) => saveAs(res, `${name}${file_type}`))
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'DocumentsFolderId.downloadError',
              defaultMessage:
                'Something went wrong with downloading "{fileName}".',
              description:
                'Error message for download failure. fileName: name of the file. Example: fileName = "contract.pdf"',
            },
            { fileName: name },
          ),
        ),
      );
  }

  handleOnDownloadOcrClick(item) {
    const { id, name } = item;
    const { intl } = this.props;

    getDocumentOCR(id)
      .then((res) => saveAs(res, `${name}.${res.type.split('/').pop()}`))
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'DocumentsFolderId.downloadError2',
              defaultMessage:
                'Something went wrong with downloading "{fileName}".',
              description:
                'Error message for download failure. fileName: name of the file. Example: fileName = "contract.pdf"',
            },
            { fileName: name },
          ),
        ),
      );
  }

  handleSaveEditColumns(fields) {
    editDocumentsViewsetFields(fields).then(() => this.loadFolderData());
  }

  handleSelectRowScreenReader(text) {
    document.querySelector('#srHolder').textContent = text;
  }

  renderPagination() {
    const { page, pageCount, pageSize } = this.state;

    if (pageCount <= pageSize) return null;

    return (
      <EcPaginate
        onPageChange={this.handlePageClick}
        pageCount={Math.ceil(pageCount / pageSize)}
        forcePage={page - 1}
      />
    );
  }

  getDocumentCountCall = () => {
    const { selectedItems, selectedItem, isAllSelected, folderData } =
      this.state;
    let folders = [];
    if (selectedItems?.length) {
      folders = selectedItems.filter((folder) => folder.type === 'folder');
    } else if (selectedItem?.type === 'folder') {
      folders = [selectedItem];
    }
    const documents = selectedItems.filter(
      (document) => document.type === 'document',
    );
    if (folders.length === 0 && !isAllSelected) {
      return {};
    } else {
      return getDocumentsCount({
        folder_id: folderData.id,
        documents: !isAllSelected
          ? documents.map((document) => document.id)
          : [],
        folders: !isAllSelected ? folders.map((folder) => folder.id) : [],
        select_all: isAllSelected,
      });
    }
  };

  getMenuItemAccessSettings = (item) => ({
    contentString: this.props.intl.formatMessage({
      id: 'DocumentsFolderId.accessSettings',
      defaultMessage: 'Access Settings',
      description: 'Menu item for access settings',
    }),
    content: (
      <Box id={OnboardingIdType.AccessSettingsTab}>
        <FormattedMessage
          id="DocumentsFolderId.accessSettings2"
          defaultMessage="Access Settings"
          description="Menu item for access settings"
        />
      </Box>
    ),
    id: `documents_row_actionAccessSettings?${item.type}=${item.id}`,
    onClick: () => this.handleShowModal(MODAL_ACCESS_SETTINGS, item),
  });

  getMenuItemDelete = (item) => {
    const { intl } = this.props;
    if (item.contains_sync_pair) {
      return {
        content: intl.formatMessage({
          id: 'DocumentsFolderId.delete',
          defaultMessage: 'Delete',
          description: 'Delete menu item',
        }),
        id: `documents_row_actionDelete?${item.type}=${item.id}`,
        disabled: true,
        tooltip: intl.formatMessage({
          id: 'DocumentsFolderId.deleteSyncTooltip',
          defaultMessage:
            'Cannot delete folders containing a synchronization with an external provider. Please contact your Evisort admin for help deleting this folder',
          description:
            'Tooltip explaining why folder with sync cannot be deleted',
        }),
      };
    } else {
      return {
        content: intl.formatMessage({
          id: 'DocumentsFolderId.delete',
          defaultMessage: 'Delete',
          description: 'Delete menu item',
        }),
        id: `documents_row_actionDelete?${item.type}=${item.id}`,
        onClick: () => this.handleShowModal(MODAL_DELETE, item),
      };
    }
  };

  getMenuItemDownload = (item) => {
    const { intl } = this.props;
    return {
      content: intl.formatMessage({
        id: 'DocumentsFolderId.download',
        defaultMessage: 'Download',
        description: 'Download menu item',
      }),
      id: `documents_row_actionDownloadOriginal?${item.type}=${item.id}`,
      onClick: () => this.handleOnDownloadOriginalClick(item),
    };
  };

  getMenuItemMove = (item) => {
    const { intl } = this.props;
    return {
      content: intl.formatMessage({
        id: 'DocumentsFolderId.move',
        defaultMessage: 'Move',
        description: 'Move menu item',
      }),
      id: `documents_row_actionMove?${item.type}=${item.id}`,
      onClick: () => this.handleShowModal(MODAL_FOLDER_TREE, item, 'Move'),
    };
  };

  getMenuItemRename = (item) => {
    const { intl } = this.props;
    return {
      content: intl.formatMessage({
        id: 'DocumentsFolderId.rename',
        defaultMessage: 'Rename',
        description: 'Rename menu item',
      }),
      id: `documents_row_actionRename?${item.type}=${item.id}`,
      onClick: () => this.handleShowModal(MODAL_NAME, item),
    };
  };

  getMenuItemAddToGroup = (item) => {
    const { intl } = this.props;
    return {
      content: intl.formatMessage({
        id: 'DocumentsFolderId.addToGroup',
        defaultMessage: 'Add to Group',
        description: 'Add to group menu item',
      }),
      id: `documents_row_actionAddToGroup?${item.type}=${item.id}`,
      onClick: () => this.handleShowModal(MODAL_DOCUMENT_GROUP, item),
    };
  };

  getMenuItems = (item) => {
    const items = [this.getMenuItemRename(item), this.getMenuItemMove(item)];
    const hasFolderAccessPermission = this.props.checkPermission(
      folderAccessPermission.resourceId,
      folderAccessPermission.resourceType,
    );
    const hasGroupedTicketAndDocs =
      this.props.flags[FlagType.GroupedTicketsAndDocs];
    if (item.type === 'document' && !hasGroupedTicketAndDocs)
      items.push(this.getMenuItemAddToGroup(item));
    if (item.type === 'document') items.push(this.getMenuItemDownload(item));
    if (item.type === 'folder' && hasFolderAccessPermission) {
      items.push(this.getMenuItemAccessSettings(item));
    }

    items.push(this.getMenuItemDelete(item));

    return items;
  };

  renderExtraOptions(item) {
    const { classes, currentUser, intl } = this.props;
    const isAdmin = testIsAdmin(currentUser);

    if (isAdmin || item.user_visibility_level === 'OPEN') {
      return (
        <div className={classes.actionsWrapper}>
          <ActionsMenu
            id={`documents_row_actions_menu?${item.type}=${item.id}`}
            items={this.getMenuItems(item, currentUser)}
            smallIcon
            title={intl.formatMessage(
              {
                id: 'DocumentsFolderId.editMenuTitle',
                defaultMessage: 'edit {itemType} menu',
                description:
                  'Title for edit menu. itemType: "document" or "folder". Example: itemType = "document"',
              },
              { itemType: item.type },
            )}
          />
        </div>
      );
    }
    return null;
  }

  renderPageSizeDropdown() {
    const { classes, intl } = this.props;
    const { pageSize } = this.state;

    return (
      <div className={classes.documentsPageSize}>
        <EcSelect
          aria-label={intl.formatMessage(
            {
              id: 'DocumentsFolderId.pageSizeAriaLabel',
              defaultMessage:
                'page size menu has {pageSize} rows per page selected',
              description:
                'Aria label for page size dropdown. pageSize: number of rows. Example: pageSize = 25',
            },
            { pageSize },
          )}
          width="200px"
          height="100%"
          options={[
            { label: 20, value: 20 },
            { label: 30, value: 30 },
            { label: 50, value: 50 },
          ]}
          defaultValue={{ label: pageSize, value: pageSize }}
          onChange={(option) => this.handlePageSizeOnChange(option.value)}
          isSearchable={false}
        />
      </div>
    );
  }

  renderFolderContent() {
    const { classes, currentUser, flags } = this.props;
    const {
      folderData,
      sortedColumn,
      sortedOrder,
      dzComponentConfig,
      dzConfig,
      dzEventHandlers,
      canModifyFolder,
      isAllSelected,
      selectedItems,
      isFetching,
      page,
      pageSize,
      pageCount,
    } = this.state;

    const configuredColumns = folderData.results.length
      ? folderData.results[0].document_fields
      : [];

    const mappedFolderData = folderData.results.length
      ? folderData.results.map((item) => {
          const mapped_document_fields =
            item && item.document_fields
              ? item.document_fields.map((item) => {
                  const fieldName = item.field_name;
                  const displayValue = item.display_value.join(' • ');
                  return {
                    [fieldName]: displayValue,
                  };
                })
              : null;

          const row = {
            ...item,
            name: item.name,
            id: item.id,
            fileType: item.file_type,
            type: item.type,
            isMovable: item.is_movable,
            folderPath: folderData.path,
            content_visibility_level: item.content_visibility_level,
            folder_access_list: {
              departments: item.folder_access_list.departments,
              members: item.folder_access_list.members,
            },
            ...mapped_document_fields,
          };

          return row;
        })
      : [];

    const dataWithMenuItems = mappedFolderData.map((row) => {
      return {
        ...row,
        actionMenu: this.renderExtraOptions(row),
      };
    });

    let sortField = 'name';
    const columnSorted = configuredColumns.filter(
      (field) => field.field_id.toString() === sortedColumn.toString(),
    );
    if (columnSorted.length) {
      sortField = columnSorted[0].field_name;
    }

    return (
      <Fragment>
        <div>
          <div className="screenReaderText" id="srHolder" aria-live="polite" />
          {canModifyFolder && featureFlagIncluded(currentUser, 'UPLOAD') ? (
            <DropzoneComponent
              className={classes.documentsDropzone}
              config={dzComponentConfig}
              djsConfig={dzConfig}
              eventHandlers={dzEventHandlers}
            >
              {flags[FlagType.DocumentsTableRevamp] ? (
                <DocumentsTableV2
                  onNavigate={this.initializeNavigationContext}
                  data={dataWithMenuItems}
                  isLoading={isFetching}
                  tableState={{
                    page,
                    pageSize,
                    sortBy: {
                      id: columnSorted.length
                        ? `${columnSorted[0].field_id}`
                        : 'name',
                      desc: sortedOrder === 'desc',
                    },
                    totalCount: pageCount,
                  }}
                  onUpdateTableState={this.updateTableState}
                  getMenuItems={this.getMenuItems}
                  handleShowModal={this.handleShowModal}
                  refetchData={this.loadFolderData}
                  onSelectAllClick={this.handleOnSelectAllClick}
                  ref={this.tableV2Ref}
                />
              ) : (
                <DocumentsTable
                  onNavigate={this.initializeNavigationContext}
                  user={currentUser}
                  folderItemsCount={folderData.count}
                  folderPath={folderData.path}
                  data={dataWithMenuItems}
                  columns={configuredColumns}
                  sortField={sortField}
                  order={sortedOrder}
                  handleShowModal={this.handleShowModal}
                  handleSortClick={this.handleSortClick}
                  handleRowSelect={this.handleRowSelect}
                  handleOnSelectPageClick={this.handleOnSelectPageClick}
                  handleOnSelectAllClick={this.handleOnSelectAllClick}
                  isAllSelected={isAllSelected}
                  handleSelectRowScreenReader={this.handleSelectRowScreenReader}
                  selectedItems={selectedItems}
                />
              )}
            </DropzoneComponent>
          ) : flags[FlagType.DocumentsTableRevamp] ? (
            <DocumentsTableV2
              onNavigate={this.initializeNavigationContext}
              data={dataWithMenuItems}
              isLoading={isFetching}
              tableState={{
                page,
                pageSize,
                sortBy: {
                  id: columnSorted.length
                    ? `${columnSorted[0].field_id}`
                    : 'name',
                  desc: sortedOrder === 'desc',
                },
                totalCount: pageCount,
              }}
              onUpdateTableState={this.updateTableState}
              getMenuItems={this.getMenuItems}
              handleShowModal={this.handleShowModal}
              refetchData={this.loadFolderData}
              onSelectAllClick={this.handleOnSelectAllClick}
              ref={this.tableV2Ref}
            />
          ) : (
            <DocumentsTable
              onNavigate={this.initializeNavigationContext}
              user={currentUser}
              folderItemsCount={folderData.count}
              folderPath={folderData.path}
              data={dataWithMenuItems}
              columns={configuredColumns}
              sortField={sortField}
              order={sortedOrder}
              handleShowModal={this.handleShowModal}
              handleSortClick={this.handleSortClick}
              handleRowSelect={this.handleRowSelect}
              handleOnSelectPageClick={this.handleOnSelectPageClick}
              handleOnSelectAllClick={this.handleOnSelectAllClick}
              isAllSelected={isAllSelected}
              handleSelectRowScreenReader={this.handleSelectRowScreenReader}
              selectedItems={selectedItems}
            />
          )}
        </div>
        <span className={classes.horizontalSeparator} />
        {!flags[FlagType.DocumentsTableRevamp] && (
          <div className={classes.documentsPaginationAndSize}>
            {this.renderPagination()}
            {this.renderPageSizeDropdown()}
          </div>
        )}
      </Fragment>
    );
  }

  renderEmptyFolder() {
    const { classes } = this.props;
    const { dzComponentConfig, dzConfig, dzEventHandlers } = this.state;

    return (
      <DropzoneComponent
        className={classes.documentsDropzone}
        config={dzComponentConfig}
        djsConfig={dzConfig}
        eventHandlers={dzEventHandlers}
      >
        <div className={classes.emptyPageWrapper}>
          <EmptyPage preset="no-documents" />
        </div>
      </DropzoneComponent>
    );
  }

  renderDocumentsColumnFieldsModal() {
    const { configuredColumns } = this.state;

    return (
      <EcModal
        modalType={MODAL_DOCUMENTS_COLUMN_FIELDS}
        width="650px"
        data={configuredColumns}
        hideModal={this.handleHideModal}
        handleOnResetClick={this.handleOnResetClick}
        handleSaveEditColumns={this.handleSaveEditColumns}
      />
    );
  }

  renderDocumentsColumnFieldsSaveModal = () => {
    const { configuredColumns } = this.state;
    const { currentUser, flags } = this.props;
    const localStorgaTableState = getStorageItem(
      getStorageKey(currentUser, TableContextType.Documents),
    );
    let fields = [];
    if (
      flags[FlagType.DocumentsTableRevamp] &&
      localStorgaTableState?.columnOrder
    ) {
      fields = localStorgaTableState.columnOrder
        .filter((column) => column !== 'name')
        .map((column, index) => ({
          column_number: index,
          field_id: isNaN(parseInt(column)) ? column : parseInt(column),
        }));
    } else {
      fields = configuredColumns.map((field, index) => ({
        column_number: index,
        field_id: field.field_id,
      }));
    }

    return (
      <BaseSaveView
        modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE}
        fields={fields}
        hideModal={this.handleHideModal}
      />
    );
  };

  renderDocumentsColumnFieldsViewSwitchModal() {
    return (
      <BaseLoadView
        modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH}
        context={undefined}
        onViewSelected={this.loadFolderData}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderNameModal() {
    const { folderData, selectedItem } = this.state;
    const { intl } = this.props;

    let title;
    let labelText;
    let confirmButtonIcon;
    let confirmButtonText;
    let currentName;
    let handleNameChange;

    if (selectedItem) {
      labelText = intl.formatMessage(
        {
          id: 'DocumentsFolderId.itemNameLabel',
          defaultMessage: '{itemType} NAME',
          description:
            'Label for item name input. itemType: "DOCUMENT" or "FOLDER". Example: itemType = "DOCUMENT"',
        },
        { itemType: selectedItem.type.toUpperCase() },
      );
      title = intl.formatMessage(
        {
          id: 'DocumentsFolderId.renameItemTitle',
          defaultMessage: 'Rename {itemType}',
          description:
            'Title for rename modal. itemType: "document" or "folder". Example: itemType = "document"',
        },
        { itemType: selectedItem.type },
      );
      confirmButtonText =
        selectedItem.type === 'document'
          ? intl.formatMessage({
              id: 'DocumentsFolderId.saveButton',
              defaultMessage: 'Save',
              description: 'Save button text',
            })
          : intl.formatMessage({
              id: 'DocumentsFolderId.renameFolderButton',
              defaultMessage: 'Rename Folder',
              description: 'Rename folder button text',
            });
      currentName = selectedItem.name;
      handleNameChange = this.onFileFolderRename;
    } else {
      labelText = intl.formatMessage({
        id: 'DocumentsFolderId.folderNameLabel',
        defaultMessage: 'FOLDER NAME',
        description: 'Label for folder name input',
      });
      title = intl.formatMessage({
        id: 'DocumentsFolderId.newFolderTitle',
        defaultMessage: 'New Folder',
        description: 'Title for new folder modal',
      });
      confirmButtonIcon = <PlusIcon size="20" color="#000" />;
      confirmButtonText = intl.formatMessage({
        id: 'DocumentsFolderId.createFolderButton',
        defaultMessage: 'Create Folder',
        description: 'Create folder button text',
      });
      currentName = '';
      handleNameChange = this.onFolderCreate;
    }

    return (
      <EcModal
        modalType={MODAL_NAME}
        width="560px"
        title={title}
        labelText={labelText}
        itemsParentFolderId={
          selectedItem?.selfUpdate ? selectedItem.parentId : folderData.id
        }
        itemsCurrentName={currentName}
        confirmButtonIcon={confirmButtonIcon}
        confirmButtonText={confirmButtonText}
        handleNameChange={handleNameChange}
        hideModal={this.handleHideModal}
        nameMaxLength={
          selectedItem.type === 'document'
            ? DOCUMENT_NAME_MAX_LENGTH
            : undefined
        }
      />
    );
  }

  updateDepartmentsArray(departmentsSelected) {
    this.setState({ departmentsSelected });
  }

  renderAccessSettingsModal() {
    const { currentUser, intl, flags } = this.props;
    const { client } = currentUser;
    const { folderData, selectedItem, departmentsSelected } = this.state;
    const folderId = selectedItem ? selectedItem.id : null;
    const enableModalConsolidation = flags[FlagType.ModalConsolidation];

    let title;
    let labelText;
    let confirmButtonIcon;
    let confirmButtonText;
    let currentName;
    let deptAccessList;
    let folderAccessList;
    let visibilityLevel;
    let handleNameChange;

    if (!!selectedItem) {
      title = selectedItem.isRoot
        ? intl.formatMessage(
            {
              id: 'DocumentsFolderId.rootAccessSettingsTitle',
              defaultMessage:
                'Access Settings for Documents {folderName} folder',
              description:
                'Title for access settings modal. folderName: name of the folder. Example: folderName = "Legal Documents"',
            },
            {
              folderName: selectedItem.name,
            },
          )
        : intl.formatMessage(
            {
              id: 'DocumentsFolderId.accessSettingsTitle',
              defaultMessage: 'Access Settings for {folderName} folder',
              description:
                'Title for access settings modal. folderName: name of the folder. Example: folderName = "Legal Documents"',
            },
            {
              folderName: selectedItem.name,
            },
          );
      confirmButtonText = intl.formatMessage({
        id: 'DocumentsFolderId.saveChangesButton',
        defaultMessage: 'Save Changes',
        description: 'Save changes button text',
      });
      currentName = selectedItem.name;
      visibilityLevel =
        selectedItem.settings_visibility_level ??
        selectedItem.content_visibility_level;
      deptAccessList = selectedItem?.folder_access_list?.departments ?? [];
      folderAccessList = selectedItem?.folder_access_list?.members ?? [];
      handleNameChange = this.onUpdateFolderAccessSettings;
    } else {
      labelText = intl.formatMessage({
        id: 'DocumentsFolderId.folderNameLabel',
        defaultMessage: 'FOLDER NAME',
        description: 'Label for folder name input',
      });
      title = intl.formatMessage({
        id: 'DocumentsFolderId.newFolderTitle',
        defaultMessage: 'New Folder',
        description: 'Title for new folder modal',
      });
      confirmButtonIcon = <PlusIcon size="20" color="#000" />;
      confirmButtonText = intl.formatMessage({
        id: 'DocumentsFolderId.createFolderButton',
        defaultMessage: 'Create Folder',
        description: 'Create folder button text',
      });
      currentName = '';
      visibilityLevel = '';
      folderAccessList = [];
      handleNameChange = this.onFolderCreate;
    }

    if (enableModalConsolidation) {
      return (
        <Panel
          enableBackdrop
          mode="floating"
          width="560px"
          title={title}
          hidden={{ onHide: this.handleHideModal }}
          footer={{
            actions: [
              {
                mode: 'chip',
                text: intl.formatMessage({
                  id: 'AccessSettingsModal.cancelButton',
                  defaultMessage: 'Cancel',
                  description: 'Cancel button text',
                }),
                onClick: this.handleHideModal,
              },
              {
                mode: 'chip',
                level: 'primary',
                text: confirmButtonText,
                onClick: async () => {
                  if (!this.accessSettingsModalRef?.handleActionClick) return;
                  try {
                    await this.accessSettingsModalRef.handleActionClick();
                  } catch (error) {
                    console.error('Error in handleActionClick:', error);
                  }
                },
              },
            ],
          }}
          children={
            <AccessSettingsModal
              hideFooter
              hideModal={this.handleHideModal}
              onGetFormDataRef={(ref) => {
                this.accessSettingsModalRef = ref;
              }}
              id={OnboardingIdType.AccessSettingsModal}
              client={client}
              user={currentUser}
              labelText={labelText}
              itemsParentFolderId={folderData.id}
              folderId={folderId}
              itemsCurrentName={currentName}
              visibilityLevel={visibilityLevel}
              deptAccessList={deptAccessList}
              folderAccessList={folderAccessList}
              confirmButtonIcon={confirmButtonIcon}
              confirmButtonText={confirmButtonText}
              handleNameChange={handleNameChange}
              departmentsSelected={departmentsSelected}
              updateDepartmentsArray={this.updateDepartmentsArray}
            />
          }
        />
      );
    }

    return (
      <EcModal
        id={OnboardingIdType.AccessSettingsModal}
        modalType={MODAL_ACCESS_SETTINGS}
        width="660px"
        client={client}
        user={currentUser}
        title={title}
        labelText={labelText}
        itemsParentFolderId={folderData.id}
        folderId={folderId}
        itemsCurrentName={currentName}
        visibilityLevel={visibilityLevel}
        deptAccessList={deptAccessList}
        folderAccessList={folderAccessList}
        confirmButtonIcon={confirmButtonIcon}
        confirmButtonText={confirmButtonText}
        handleNameChange={handleNameChange}
        hideModal={this.handleHideModal}
        departmentsSelected={departmentsSelected}
        updateDepartmentsArray={this.updateDepartmentsArray}
      />
    );
  }

  renderAddToGroupModal() {
    const { selectedItem, selectedItems, isAllSelected } = this.state;
    const { currentUser, intl } = this.props;
    const { folderId } = this.props.params;
    const [documents] = this.setBulkParams();

    let documentIds = [];
    if (selectedItems.length) {
      documentIds = selectedItems.map((item) =>
        item.original ? item.original.document_id : item.document_id,
      );
    } else if (selectedItem) {
      documentIds = [selectedItem.document_id];
    }

    return featureFlagIncluded(
      currentUser,
      FeatureFlagType.DocumentGroupRevamp,
    ) ? (
      <AddToGroup
        hideModal={this.handleHideModal}
        documentIds={isAllSelected ? undefined : documentIds}
        folderId={isAllSelected ? folderId : undefined}
        reloadDocumentContent={() => this.updatePageAfterBulkAction(documents)}
      />
    ) : (
      <EcModal
        modalType={MODAL_DOCUMENT_GROUP}
        width="660px"
        title={intl.formatMessage({
          id: 'DocumentsFolderId.addToGroupTitle',
          defaultMessage: 'Add to Group',
          description: 'Title for add to group modal',
        })}
        selectedItems={selectedItem ? selectedItem : selectedItems}
        documentIds={documentIds}
        folderId={isAllSelected ? folderId : null}
        hideModal={this.handleHideModal}
        reloadDocumentContent={() => this.updatePageAfterBulkAction(documents)}
      />
    );
  }

  renderBulkDeleteModalText() {
    const { folderData, selectedItems, isAllSelected } = this.state;

    if (!selectedItems && !isAllSelected) return null;

    const itemsCount = isAllSelected ? folderData.count : selectedItems.length;

    return (
      <FormattedMessage
        id="DocumentsFolderId.bulkDeleteConfirmation"
        defaultMessage="Are you sure you want to delete the {itemsCount} {itemsCount, plural, one {item} other {items}} you have selected? You won't be able to undo this action."
        description="Confirmation message for bulk delete. Example: itemsCount = '5', items = 'documents'"
        values={{ itemsCount }}
      />
    );
  }

  renderDeleteDocumentModalText() {
    return (
      <FormattedMessage
        id="DocumentsFolderId.deleteDocumentConfirmation"
        defaultMessage="Are you sure you want to delete <span>{itemName}</span>? You won't be able to undo this action."
        description="Confirmation message for document delete. Example: itemName = 'Contract.pdf'"
        values={{
          span: (chunks) => <span>{chunks}</span>,
          itemName: this.state.selectedItem.name,
        }}
      />
    );
  }

  renderDeleteFolderModalText() {
    return (
      <FormattedMessage
        id="DocumentsFolderId.deleteFolderConfirmation"
        defaultMessage="Are you sure you want to delete <span>{itemName}</span>? Deleting this folder will also delete the documents it contains. You won't be able to undo this action."
        description="Confirmation message for folder delete. Example: itemName = 'Legal Contracts'"
        values={{
          span: (chunks) => <span>{chunks}</span>,
          itemName: this.state.selectedItem.name,
        }}
      />
    );
  }

  renderDeleteModal() {
    const {
      selectedItem,
      selectedItems,
      primaryModal,
      isAllSelected,
      folderData,
    } = this.state;
    const { currentUser, intl } = this.props;
    const itemsCount = isAllSelected ? folderData.count : selectedItems?.length;

    const documentDeleteReasonRequired =
      !!currentUser?.client_config?.document_delete_reason_required;

    if (documentDeleteReasonRequired) {
      const count = selectedItems?.length ? itemsCount : 1;
      let type = 'item';
      let label = '';
      if (count === 1) {
        if (selectedItems?.length) {
          type = selectedItems[0]?.type;
          label = `"${selectedItems[0]?.name}"`;
        } else if (selectedItem) {
          type = selectedItem.type;
          label = `"${selectedItem?.name}"`;
        }
      }
      return (
        <DeleteDocumentWithReasonModal
          type={selectedItem?.selfUpdate ? selectedItem.type : type}
          isVisible={primaryModal === MODAL_DELETE}
          count={count}
          selectedItemName={label}
          fetchDocumentsCount={this.getDocumentCountCall}
          hideModal={this.handleHideModal}
          onConfirmDelete={this.handleOnConfirmDelete}
        />
      );
    }

    let modalTitle;
    let modalText;
    if (selectedItems.length) {
      modalTitle = intl.formatMessage({
        id: 'DocumentsFolderId.deleteItemsTitle',
        defaultMessage: 'Delete Items?',
        description: 'Title for bulk delete modal',
      });
      modalText = this.renderBulkDeleteModalText();
    } else {
      modalTitle = intl.formatMessage(
        {
          id: 'DocumentsFolderId.deleteItemTitle',
          defaultMessage: 'Delete {itemType}?',
          description:
            'Title for single item delete modal. itemType: . Example: itemType = "document""document" or "folder". Example: itemType = "document"',
        },
        { itemType: selectedItem.type },
      );
      modalText =
        selectedItem.type === 'document'
          ? this.renderDeleteDocumentModalText()
          : this.renderDeleteFolderModalText();
    }

    return (
      <Modal
        isVisible={true}
        children={<Text>{modalText}</Text>}
        primaryAction={{
          disabled: itemsCount > BULK_DELETE_LIMIT,
          text: intl.formatMessage({
            id: 'DocumentsFolderId.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text',
          }),
          tooltip: intl.formatMessage({
            id: 'DocumentsFolderId.deleteLimitTooltip',
            defaultMessage:
              'Only 5 documents can be deleted at a time. Please select fewer documents to complete this action.',
            description: 'Tooltip explaining delete limit',
          }),
          onClick: () => {
            this.onBulkDelete();
            this.handleHideModal();
          },
          variant: 'danger',
        }}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        title={modalTitle}
      />
    );
  }

  renderFolderTreeBulkModal() {
    const {
      folderTree,
      uploadLocation,
      modalBulkAction,
      selectedItems,
      selectedItem,
      isAllSelected,
      folderData,
    } = this.state;
    const { intl } = this.props;
    const bulkAction = modalBulkAction ?? selectedItem.action;
    const modalTitle = intl.formatMessage(
      {
        id: 'DocumentsFolderId.moveToFolderTitle',
        defaultMessage: '{action} to Folder',
        description:
          'Title for move/copy to folder modal. action: "Move" or "Copy". Example: action = "Move"',
      },
      { action: bulkAction },
    );

    // Prevent selection of sync_paired folders iff the selectedItem(s) all contain sync pairs
    const onlySyncPairedItemsSelected =
      selectedItem?.contains_sync_pair ||
      (selectedItems.every((item) => item.contains_sync_pair) &&
        selectedItems.length);
    const disableSyncPaired =
      bulkAction === 'Move' && onlySyncPairedItemsSelected;
    const limitCountPerActionType =
      bulkAction === 'Move' ? BULK_MOVE_LIMIT : BULK_COPY_LIMIT;
    const itemsCount = isAllSelected ? folderData.count : selectedItems?.length;

    return (
      <EcModal
        modalType={MODAL_FOLDER_TREE}
        bulkAction={bulkAction}
        overLimit={itemsCount > limitCountPerActionType}
        width="540px"
        title={modalTitle}
        folderTree={folderTree}
        folderIdsSelected={[]}
        fetchDocumentsCount={this.getDocumentCountCall}
        uploadLocation={uploadLocation}
        handleUploadLocationChange={this.onBulkMoveCopy}
        confirmButtonText={bulkAction}
        hideModal={this.handleHideModal}
        disableSyncPaired={disableSyncPaired}
        enableSearch={true}
      />
    );
  }

  renderFolderTreeUploadModal() {
    const { uploadLocation } = this.state;
    const { intl } = this.props;

    return (
      <EcModal
        modalType={MODAL_FOLDER_TREE}
        width="540px"
        title={intl.formatMessage({
          id: 'DocumentsFolderId.chooseLocationTitle',
          defaultMessage: 'Choose Location',
          description: 'Title for choose location modal',
        })}
        uploadLocation={uploadLocation}
        handleUploadLocationChange={this.handleUploadLocationChange}
        confirmButtonIcon={<CheckmarkIcon size="20" />}
        confirmButtonText={intl.formatMessage({
          id: 'DocumentsFolderId.chooseLocationButton',
          defaultMessage: 'Choose Location',
          description: 'Button text for choosing location',
        })}
        hideModal={this.handleHideSecondaryModal}
        enableSearch={true}
      />
    );
  }

  renderUploadModal() {
    const { uploadFiles, uploadLocation, hasUploadPermission } = this.state;
    const { currentUser, uploadDocumentsThunk, intl } = this.props;

    const extendedUploadFiles = uploadFiles.map((file) => {
      file.folderId = uploadLocation.id;
      return file;
    });

    return (
      <EcModal
        key="primary-modal"
        modalType={MODAL_UPLOAD}
        width="755px"
        title={intl.formatMessage({
          id: 'DocumentsFolderId.uploadTitle',
          defaultMessage: 'Upload',
          description: 'Title for upload modal',
        })}
        uploadLocationName={uploadLocation.name}
        uploadLocationPath={uploadLocation.path}
        hasUploadPermission={hasUploadPermission}
        uploadFiles={uploadFiles}
        confirmButtonText={intl.formatMessage({
          id: 'DocumentsFolderId.uploadDocumentsButton',
          defaultMessage: 'Upload Documents',
          description: 'Button text for uploading documents',
        })}
        handleAddUploadFiles={this.handleAddUploadFiles}
        handleRemoveUploadFile={this.handleRemoveUploadFile}
        hideModal={this.handleHideModal}
        handleUploadFiles={(formFields) => {
          trackSegment('User Uploads Document', {
            fileNames: extendedUploadFiles.map((file) => file.name),
            groupId: getClientInfo(currentUser),
            userId: getUserClientInfo(currentUser),
          });
          (async () => {
            await uploadDocumentsThunk({
              files: extendedUploadFiles,
              uploadFormFields: formFields,
              apiConfig: {
                service: 'pilot',
                method: 'post',
                url: '/document/upload/',
              },
            });

            this.loadFolderData();
          })();
        }}
        showSecondaryModal={this.handleShowSecondaryModal}
      />
    );
  }

  handleDepartmentChange(departmentsSelected) {
    this.setState({ departmentsSelected });
  }

  renderDepartmentTreeModal() {
    const {
      currentUser: { client },
      intl,
    } = this.props;
    const { selectedItem, departmentsSelected } = this.state;
    const deptsSelected =
      departmentsSelected ||
      selectedItem?.original?.folder_access_list?.departments;

    return (
      <EcModal
        modalType={MODAL_DEPARTMENT_TREE}
        width="540px"
        client={client}
        title={intl.formatMessage({
          id: 'DocumentsFolderId.selectDepartmentTitle',
          defaultMessage: 'Select a Department',
          description: 'Title for select department modal',
        })}
        departmentsSelected={deptsSelected}
        handleDepartmentChange={this.handleDepartmentChange}
        confirmButtonIcon={<CheckmarkIcon size="20" />}
        confirmButtonText={intl.formatMessage({
          id: 'DocumentsFolderId.selectDepartmentButton',
          defaultMessage: 'Select Department',
          description: 'Button text for selecting department',
        })}
        hideModal={this.handleHideSecondaryModal}
      />
    );
  }

  renderMultiEditModal() {
    const { folderData, selectedItems } = this.state;
    const [documents, folders, select_all] = this.setBulkParams();
    const itemsCount = select_all ? folderData.count : selectedItems.length;
    return (
      <EcModal
        modalType={MODAL_MULTI_EDIT}
        width="800px"
        hideModal={this.handleHideModal}
        fetchDocumentsCount={this.getDocumentCountCall}
        getModalData={() =>
          getMultiEditData(folderData.id, documents, folders, select_all)
        }
        overLimit={itemsCount > BULK_EDIT_LIMIT}
        update={(fieldsData) =>
          bulkUpdate(folderData.id, {
            documentFields: fieldsData,
            select_all,
            documents,
            folders,
          })
        }
        onUpdateComplete={() => this.updatePageAfterBulkAction(documents)}
      />
    );
  }

  shouldRenderLoadingState = () => {
    const { flags } = this.props;
    const { isLoading, isFetching } = this.state;
    return (!flags[FlagType.DocumentsTableRevamp] && isFetching) || isLoading;
  };

  render() {
    const { params, classes, currentUser, intl } = this.props;
    const {
      folderData,
      primaryModal,
      secondaryModal,
      isLoading,
      errorLoadingMessage,
      canModifyFolder,
    } = this.state;

    return (
      <div className={classes.documentsPage}>
        <DocumentsPageHeader
          handleShowModal={this.handleShowModal}
          handleAddUploadFiles={this.handleAddUploadFiles}
          handleSort={this.handleSortOnChange}
          folderPathId={params.folderId}
          user={currentUser}
          folderData={folderData}
          loading={isLoading}
          canModifyFolder={canModifyFolder}
        />
        {this.shouldRenderLoadingState() && !errorLoadingMessage ? (
          <div className={classes.loadingContainer}>
            <LoadingSpinner size="medium" />
          </div>
        ) : !isLoading && errorLoadingMessage ? (
          <div className={classes.errorLoadingWrapper}>
            <div className={classes.errorLoadingMessage}>
              <FormattedMessage
                id="DocumentsFolderId.errorLoadingDocuments"
                defaultMessage="An error occurred when loading the documents."
                description="Error message when documents fail to load"
              />
            </div>
            <Button
              text={intl.formatMessage({
                id: 'DocumentsFolderId.reloadPageButton',
                defaultMessage: 'Reload Page',
                description: 'Button text to reload the page',
              })}
              onClick={this.reloadPage}
            />
          </div>
        ) : !isLoading &&
          folderData &&
          folderData.results &&
          folderData.results.length ? (
          this.renderFolderContent()
        ) : (
          this.renderEmptyFolder()
        )}
        {primaryModal === MODAL_ACCESS_SETTINGS
          ? this.renderAccessSettingsModal()
          : null}
        {primaryModal === MODAL_NAME ? this.renderNameModal() : null}
        {primaryModal === MODAL_DOCUMENT_GROUP
          ? this.renderAddToGroupModal()
          : null}
        {primaryModal === MODAL_DELETE ? this.renderDeleteModal() : null}
        {primaryModal === MODAL_UPLOAD ? this.renderUploadModal() : null}
        {primaryModal === MODAL_FOLDER_TREE
          ? this.renderFolderTreeBulkModal()
          : null}
        {primaryModal === MODAL_MULTI_EDIT ? this.renderMultiEditModal() : null}
        {primaryModal === MODAL_DOCUMENTS_COLUMN_FIELDS
          ? this.renderDocumentsColumnFieldsModal()
          : null}
        {primaryModal === MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE
          ? this.renderDocumentsColumnFieldsSaveModal()
          : null}
        {primaryModal === MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH
          ? this.renderDocumentsColumnFieldsViewSwitchModal()
          : null}
        {secondaryModal === MODAL_FOLDER_TREE
          ? this.renderFolderTreeUploadModal()
          : null}
        {secondaryModal === MODAL_DEPARTMENT_TREE
          ? this.renderDepartmentTreeModal()
          : null}
      </div>
    );
  }
}

Page.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = ({ currentUser }) => ({
  currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  uploadDocumentsThunk: (...args) => dispatch(uploadDocumentsThunk(...args)),
  initContext: (...args) => dispatch(initContext(...args)),
  setAskAIContext: (payload) => dispatch(chatbot.actions.setContext(payload)),
});

Page = connect(mapStateToProps, mapDispatchToProps)(Page);

export default injectSheet(styles)(
  withFlags(withRouting(withPermissions(injectIntl(Page), permissionsToCheck))),
);
